import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAdminGetSupplyBeamConfigListMutation,
  useAdminCreateSupplyBeamConfigMutation,
  useAdminUpdateSupplyBeamConfigMutation,
  useAdminToggleSupplyBeamConfigStatusMutation,
  useAdminReorderSupplyBeamConfigMutation,
} from "../../../features/admin/supplyBeamConfig/supplyBeamConfigApi";
import {
  FiPlus,
  FiEdit2,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUp,
  FiArrowDown,
  FiCheckCircle,
  FiXCircle,
  FiLayers,
  FiShield,
  FiBox,
  FiX,
  FiSearch,
  FiNavigation,
  FiActivity,
  FiCheck,
  FiSettings,
  FiRefreshCw,
  FiTruck,
  FiPackage,
  FiLogIn,
  FiLogOut,
  FiRepeat,
  FiZap,
  FiLink,
  FiAlertCircle,
  FiEye,
  FiGrid,
  FiList,
  FiSliders,
  FiCornerDownRight,
  FiSave,
  FiCheckSquare,
} from "react-icons/fi";
import {
  MdOutlineLinearScale,
  MdOutlineFactory,
  MdWarehouse,
  MdLocalShipping,
  MdStorefront,
  MdOutlineAltRoute,
  MdLocationOn,
  MdSync,
  MdOutlineHub,
  MdAutoGraph,
  MdOutlineRule,
  MdOutlinePalette,
} from "react-icons/md";
import SupplyBeamThemeConfig from "./SupplyBeamThemeConfig";

// ─────────────────────────────────────────────────
// Contextual Icons for stages
// ─────────────────────────────────────────────────
const getStageIcon = (name = "") => {
  const lower = name.toLowerCase();
  if (lower.includes("plant") || lower.includes("factory") || lower.includes("manufactur") || lower.includes("prod"))
    return <MdOutlineFactory size={22} />;
  if (lower.includes("warehouse") || lower.includes("storage") || lower.includes("dc"))
    return <MdWarehouse size={22} />;
  if (lower.includes("distributor") || lower.includes("transit") || lower.includes("logistics") || lower.includes("hub"))
    return <MdLocalShipping size={22} />;
  if (lower.includes("depot") || lower.includes("store") || lower.includes("retail") || lower.includes("shop"))
    return <MdStorefront size={22} />;
  return <MdOutlineAltRoute size={22} />;
};

// ─────────────────────────────────────────────────
// Default Workflow Config Structure
// ─────────────────────────────────────────────────
const defaultWorkflowConfig = {
  mapping: {
    mapping_type: "direct", // "direct" | "attribute_based"
    direct_product_code: true, // product_code to product_code
    by_size: false,            // on the basis of size
    by_qty: false,             // on the basis of qty
    by_color: false,           // on the basis of color
    allow_mixed_attributes: false,
    strict_product_code: true,
  },
  checkin: {
    through_parent: false,
    through_child: false,
    require_order_number: false,
    require_invoice_number: false,
    require_picklist_number: false,
    only_if_checked_out: false,
    allow_transporter_details: false,
  },
  checkout: {
    order: {
      enabled: false,
      scan_against_order: false,
      save_picklist_against_order: false,
      enter_invoice_no: false,
      checkout_through_invoice: false,
      scan_multiple_picklists: false,
      enable_partial_dispatch: false,
    },
    carton: {
      enabled: false,
      scan_and_save_picklist: false,
      scan_multiple_cartons_against_picklist: false,
      checkout_carton: false,
      enable_partial_dispatch: false,
    },
    direct: {
      enabled: false,
      enter_order_and_checkout_directly: false,
    },
  },
  sync: {
    order_sync: "manual",
    carton_sync: "manual",
    invoice_sync: "manual",
    picklist_sync: "manual",
  },
  return: {
    return_through_order: false,
    return_through_invoice: false,
    return_through_picklist: false,
    return_through_carton: false,
    return_to_n_minus_1: false,
    return_to_any_location: false,
    allow_child_return: false,
  },
};

const emptyForm = {
  id: null,
  location_type: "",
  location_type_code: "",
  order_index: 1,
  checkin_allowed: 1,
  checkout_allowed: 1,
  return_allowed: 0,
  qr_mapping_allowed: 0,
  qr_demapping_allowed: 0,
  geofence_radius: 500,
  max_capacity: "",
  allowed_checkout_types: [],
  workflow_config: JSON.parse(JSON.stringify(defaultWorkflowConfig)),
  status: "1",
};

// ─────────────────────────────────────────────────
// Custom Table Design Tokens
// ─────────────────────────────────────────────────
const customTableStyles = {
  table: { style: { backgroundColor: "#ffffff" } },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderTop: "1px solid #edf2f7",
      borderBottom: "1px solid #e2e8f0",
      minHeight: "48px",
    },
  },
  headCells: {
    style: {
      fontSize: "12px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      color: "#475569",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "68px",
      fontSize: "13px",
      color: "#1e293b",
      borderBottom: "1px solid #f1f5f9",
      "&:hover": { backgroundColor: "#f8fafc", transition: "all 0.15s ease" },
    },
  },
  cells: {
    style: { paddingLeft: "16px", paddingRight: "16px" },
  },
};

// ─────────────────────────────────────────────────
// Reusable UI Elements
// ─────────────────────────────────────────────────
const Toggle = ({ value, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    style={{
      width: "46px",
      height: "26px",
      borderRadius: "13px",
      border: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      background: value ? "linear-gradient(135deg,#4f46e5,#6366f1)" : "#cbd5e1",
      position: "relative",
      transition: "all 0.25s ease",
      flexShrink: 0,
      boxShadow: value ? "0 2px 8px rgba(79,70,229,0.3)" : "none",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: "3px",
        left: value ? "23px" : "3px",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "all 0.2s ease",
      }}
    />
  </button>
);

const SyncPicker = ({ label, value, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      background: "#fff",
      borderRadius: "10px",
      border: "1px solid #e2e8f0",
      marginBottom: "10px",
    }}
  >
    <span style={{ fontSize: "14px", color: "#1e293b", fontWeight: 600 }}>{label}</span>
    <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
      {["manual", "erp"].map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          style={{
            padding: "6px 18px",
            border: "none",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            background: value === opt ? "linear-gradient(135deg,#4f46e5,#6366f1)" : "#f8fafc",
            color: value === opt ? "#fff" : "#64748b",
            transition: "all 0.2s",
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const OptionRow = ({ label, description, value, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      padding: "14px 16px",
      background: "#fff",
      borderRadius: "10px",
      border: "1px solid #e2e8f0",
      marginBottom: "10px",
      gap: "16px",
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#1e293b" }}>{label}</div>
      {description && (
        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "3px", lineHeight: "1.4" }}>
          {description}
        </div>
      )}
    </div>
    <Toggle value={value} onChange={onChange} />
  </div>
);

const SectionCard = ({ icon, title, description, accent = "#4f46e5", children }) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "14px",
      overflow: "hidden",
      marginBottom: "20px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    }}
  >
    <div
      style={{
        padding: "16px 20px",
        background: `linear-gradient(135deg, ${accent}0d, ${accent}04)`,
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: `${accent}15`,
            color: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </span>
        <div>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>{title}</span>
          {description && (
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "1px" }}>{description}</div>
          )}
        </div>
      </div>
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);

// ─────────────────────────────────────────────────
// Workflow Config Tabs Definition
// ─────────────────────────────────────────────────
const WORKFLOW_TABS = [
  { id: "basic", label: "Basic Settings", icon: <FiSettings size={16} /> },
  { id: "mapping", label: "QR Mapping Flow", icon: <FiLayers size={16} /> },
  { id: "checkin", label: "Check-In Flow", icon: <FiLogIn size={16} /> },
  { id: "checkout", label: "Checkout Flow", icon: <FiLogOut size={16} /> },
  { id: "sync", label: "Data & ERP Sync", icon: <MdSync size={16} /> },
  { id: "return", label: "Return Flow", icon: <FiRepeat size={16} /> },
];

// ─────────────────────────────────────────────────
// Tab Views
// ─────────────────────────────────────────────────
const TabBasicInfo = ({ formData, setFormData, stages }) => {
  const setField = (key, val) => setFormData((prev) => ({ ...prev, [key]: val }));

  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    border: "1.5px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "13.5px",
    color: "#1e293b",
    background: "#fff",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: 700,
    color: "#334155",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "7px",
    display: "block",
  };

  const SegToggle = ({ label, field }) => (
    <div>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #cbd5e1", width: "fit-content" }}>
        {[{ label: "Enabled", val: 1 }, { label: "Disabled", val: 0 }].map((opt) => (
          <button
            key={opt.val}
            type="button"
            onClick={() => setField(field, opt.val)}
            style={{
              padding: "9px 20px",
              border: "none",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 700,
              background:
                formData[field] === opt.val
                  ? opt.val === 1
                    ? "linear-gradient(135deg,#10b981,#059669)"
                    : "linear-gradient(135deg,#ef4444,#dc2626)"
                  : "#f8fafc",
              color: formData[field] === opt.val ? "#fff" : "#64748b",
              transition: "all 0.2s",
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  const toggleCheckoutDest = (targetId) => {
    const current = [...formData.allowed_checkout_types];
    const exists = current.includes(targetId);
    setFormData({
      ...formData,
      allowed_checkout_types: exists
        ? current.filter((id) => id !== targetId)
        : [...current, targetId],
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <SectionCard
        icon={<FiSettings size={16} />}
        title="Stage Identification"
        description="Name, sequence order index, and stage code identifier"
        accent="#4f46e5"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
          <div>
            <label style={labelStyle}>Location Stage Name *</label>
            <input
              style={inputStyle}
              value={formData.location_type}
              onChange={(e) => setField("location_type", e.target.value)}
              placeholder="e.g. Plant, Warehouse, Distributor, Depot"
            />
          </div>
          <div>
            <label style={labelStyle}>Stage Code Identifier</label>
            <input
              style={inputStyle}
              value={formData.location_type_code}
              onChange={(e) => setField("location_type_code", e.target.value)}
              placeholder="e.g. PLT, WH, DIST, DEP"
            />
          </div>
          <div>
            <label style={labelStyle}>Linear Order Index</label>
            <input
              style={inputStyle}
              type="number"
              value={formData.order_index}
              onChange={(e) => setField("order_index", e.target.value)}
              min={1}
            />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #cbd5e1", width: "fit-content" }}>
              {[{ label: "Active", val: "1" }, { label: "Inactive", val: "0" }].map((opt) => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setField("status", opt.val)}
                  style={{
                    padding: "9px 20px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 700,
                    background:
                      formData.status === opt.val
                        ? opt.val === "1"
                          ? "linear-gradient(135deg,#10b981,#059669)"
                          : "linear-gradient(135deg,#ef4444,#dc2626)"
                        : "#f8fafc",
                    color: formData.status === opt.val ? "#fff" : "#64748b",
                    transition: "all 0.2s",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={<FiShield size={16} />}
        title="Physical Boundary & Capacity"
        description="Geofence verification radius and facility capacity limits"
        accent="#0ea5e9"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div>
            <label style={labelStyle}>Geofence Radius (meters)</label>
            <input
              style={inputStyle}
              type="number"
              value={formData.geofence_radius}
              onChange={(e) => setField("geofence_radius", e.target.value)}
              min={0}
              placeholder="500"
            />
            <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "block" }}>
              Mobile scan allowed within this distance of facility
            </span>
          </div>
          <div>
            <label style={labelStyle}>Max Capacity (Units)</label>
            <input
              style={inputStyle}
              type="number"
              value={formData.max_capacity}
              onChange={(e) => setField("max_capacity", e.target.value)}
              placeholder="Leave blank for unlimited"
            />
            <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "block" }}>
              Maximum inventory holding threshold
            </span>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={<FiLayers size={16} />}
        title="Core Capabilities & QR Hierarchy"
        description="Enable or disable inbound, outbound, returns, and QR serialization mapping operations"
        accent="#10b981"
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" }}>
          <SegToggle label="Check-In Allowed" field="checkin_allowed" />
          <SegToggle label="Checkout Allowed" field="checkout_allowed" />
          <SegToggle label="Return Allowed" field="return_allowed" />
          <SegToggle label="QR Mapping Allowed" field="qr_mapping_allowed" />
          <SegToggle label="QR Demapping Allowed" field="qr_demapping_allowed" />
        </div>
        {Number(formData.qr_mapping_allowed) === 1 && (
          <div
            style={{
              marginTop: "14px",
              padding: "10px 14px",
              borderRadius: "8px",
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              fontSize: "12px",
              color: "#0369a1",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiLayers size={15} style={{ flexShrink: 0 }} />
            <span>
              QR Mapping is <strong>enabled</strong>. Configure whether packaging mapping is <strong>Direct (product_code)</strong> or <strong>Attribute-based (Size, Qty, Color)</strong> in the <strong>QR Mapping Flow</strong> tab.
            </span>
          </div>
        )}
      </SectionCard>

      {/* Checkout destinations */}
      {stages.length > 1 && (
        <SectionCard
          icon={<FiCornerDownRight size={16} />}
          title="Allowed Outbound Destinations"
          description="Specify which subsequent supply chain locations items can be checked out to"
          accent="#8b5cf6"
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {stages
              .filter((s) => s.id !== formData.id)
              .map((s) => {
                const selected = formData.allowed_checkout_types.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleCheckoutDest(s.id)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "10px",
                      border: `2px solid ${selected ? "#4f46e5" : "#cbd5e1"}`,
                      background: selected ? "#4f46e50f" : "#f8fafc",
                      color: selected ? "#4f46e5" : "#475569",
                      fontSize: "13px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "all 0.2s",
                    }}
                  >
                    {selected ? <FiCheck size={14} /> : <FiPlus size={14} />}
                    {s.location_type}
                    {s.location_type_code && (
                      <span style={{ fontSize: "10px", opacity: 0.7 }}>({s.location_type_code})</span>
                    )}
                  </button>
                );
              })}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

const TabMapping = ({ formData, workflow, setWorkflow }) => {
  const mapping = workflow?.mapping || defaultWorkflowConfig.mapping;

  const setMapping = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      mapping: { ...(prev?.mapping || defaultWorkflowConfig.mapping), [key]: val },
    }));

  const isMappingAllowed = Number(formData.qr_mapping_allowed) === 1;

  return (
    <div>
      {!isMappingAllowed && (
        <div
          style={{
            padding: "16px 20px",
            background: "#fef9c3",
            border: "1px solid #fde047",
            borderRadius: "12px",
            marginBottom: "20px",
            fontSize: "13.5px",
            color: "#854d0e",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FiAlertCircle size={20} style={{ flexShrink: 0 }} />
          <span>
            QR Mapping is currently <strong>disabled</strong> on this stage. To activate this workflow and map child items to parent packaging, enable{" "}
            <strong>&ldquo;QR Mapping Allowed&rdquo;</strong> in the <strong>Basic Settings</strong> tab.
          </span>
        </div>
      )}

      {/* Mapping Mode & Strategy */}
      <SectionCard
        icon={<FiLayers size={16} />}
        title="Mapping Mode & Strategy"
        description="Select whether parent-child packaging mapping is direct by product code or determined by product variant attributes"
        accent="#0284c7"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
          {/* Direct Option */}
          <div
            onClick={() => {
              setMapping("mapping_type", "direct");
              setMapping("direct_product_code", true);
            }}
            style={{
              padding: "18px 20px",
              borderRadius: "12px",
              border: `2px solid ${mapping.mapping_type === "direct" ? "#0284c7" : "#e2e8f0"}`,
              background: mapping.mapping_type === "direct" ? "#f0f9ff" : "#ffffff",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: mapping.mapping_type === "direct" ? "#0284c7" : "#e2e8f0",
                    color: mapping.mapping_type === "direct" ? "#fff" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  <FiCheckSquare size={16} />
                </span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                  Direct Mapping
                </span>
              </div>
              <input
                type="radio"
                name="mapping_type"
                checked={mapping.mapping_type === "direct"}
                onChange={() => {
                  setMapping("mapping_type", "direct");
                  setMapping("direct_product_code", true);
                }}
                style={{ accentColor: "#0284c7", width: "18px", height: "18px", cursor: "pointer" }}
              />
            </div>
            <div style={{ fontSize: "12.5px", color: "#475569", lineHeight: "1.5" }}>
              <strong>product_code → product_code</strong>: 1-to-1 direct mapping where child item SKU directly matches the parent carton packaging SKU.
            </div>
            <div style={{ marginTop: "12px", display: "flex", gap: "6px" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#e0f2fe", color: "#0369a1" }}>
                Direct SKU Match
              </span>
            </div>
          </div>

          {/* Attribute-Based Option */}
          <div
            onClick={() => setMapping("mapping_type", "attribute_based")}
            style={{
              padding: "18px 20px",
              borderRadius: "12px",
              border: `2px solid ${mapping.mapping_type === "attribute_based" ? "#7c3aed" : "#e2e8f0"}`,
              background: mapping.mapping_type === "attribute_based" ? "#f5f3ff" : "#ffffff",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: mapping.mapping_type === "attribute_based" ? "#7c3aed" : "#e2e8f0",
                    color: mapping.mapping_type === "attribute_based" ? "#fff" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  <FiLayers size={16} />
                </span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                  Attribute-Based Mapping
                </span>
              </div>
              <input
                type="radio"
                name="mapping_type"
                checked={mapping.mapping_type === "attribute_based"}
                onChange={() => setMapping("mapping_type", "attribute_based")}
                style={{ accentColor: "#7c3aed", width: "18px", height: "18px", cursor: "pointer" }}
              />
            </div>
            <div style={{ fontSize: "12.5px", color: "#475569", lineHeight: "1.5" }}>
              <strong>On the basis of Size, Qty, or Color</strong>: Multi-attribute packaging validation where child items are validated against variant criteria.
            </div>
            <div style={{ marginTop: "12px", display: "flex", gap: "6px" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#ede9fe", color: "#6d28d9" }}>
                Size / Qty / Color
              </span>
            </div>
          </div>
        </div>

        <OptionRow
          label="Direct Product Code Match (product_code → product_code)"
          description="Strictly require identical product_code on child QR and parent packaging QR"
          value={mapping.direct_product_code}
          onChange={(v) => setMapping("direct_product_code", v)}
        />
      </SectionCard>

      {/* Attribute Rules: Size, Qty, Color */}
      <SectionCard
        icon={<FiSliders size={16} />}
        title="Attribute Verification Criteria"
        description="Select which product attributes are required and validated when mapping child QRs into parent packaging"
        accent="#8b5cf6"
      >
        <OptionRow
          label="Mapping on the Basis of Size"
          description="Enforce that child units mapped to this carton match the designated size variant (e.g. S, M, L, XL, UK 9, 32W)"
          value={mapping.by_size}
          onChange={(v) => setMapping("by_size", v)}
        />
        <OptionRow
          label="Mapping on the Basis of Quantity"
          description="Enforce strict carton capacity check (e.g. exactly 10 units / 24 units per carton) before packaging is sealed"
          value={mapping.by_qty}
          onChange={(v) => setMapping("by_qty", v)}
        />
        <OptionRow
          label="Mapping on the Basis of Color"
          description="Enforce that all child units mapped into the parent box share identical product shade / color code"
          value={mapping.by_color}
          onChange={(v) => setMapping("by_color", v)}
        />
      </SectionCard>

      {/* Validation & Packaging Rules */}
      <SectionCard
        icon={<FiShield size={16} />}
        title="Packaging Rules & Safeguards"
        description="Catalog validation and carton packaging rules"
        accent="#059669"
      >
        <OptionRow
          label="Allow Mixed Attributes in Carton"
          description="Permit packing multiple sizes or colors inside the same carton if batch allows"
          value={mapping.allow_mixed_attributes}
          onChange={(v) => setMapping("allow_mixed_attributes", v)}
        />
        <OptionRow
          label="Strict Product Catalog Validation"
          description="Reject scanning if child QR product code does not exist in the active product catalog"
          value={mapping.strict_product_code}
          onChange={(v) => setMapping("strict_product_code", v)}
        />
      </SectionCard>
    </div>
  );
};

const TabCheckin = ({ workflow, setWorkflow }) => {
  const set = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      checkin: { ...prev.checkin, [key]: val },
    }));

  return (
    <div>
      <SectionCard
        icon={<FiLink size={16} />}
        title="Check-In Hierarchy"
        description="Configure whether scanning operates on packaging hierarchy"
        accent="#4f46e5"
      >
        <OptionRow
          label="Check-In Through Parent QR"
          description="Scanning the parent QR code automatically checks in all packed child units"
          value={workflow.checkin.through_parent}
          onChange={(v) => set("through_parent", v)}
        />
        <OptionRow
          label="Check-In Through Child QR"
          description="Allow scanning individual child SKU QR codes for granular inbound logging"
          value={workflow.checkin.through_child}
          onChange={(v) => set("through_child", v)}
        />
      </SectionCard>

      <SectionCard
        icon={<FiAlertCircle size={16} />}
        title="Mandatory Inbound Verifications"
        description="Prompt operators for ERP reference numbers before allowing check-in"
        accent="#f59e0b"
      >
        <OptionRow
          label="Require Order Number"
          description="Operator must enter or scan a valid Order Number to initiate check-in"
          value={workflow.checkin.require_order_number}
          onChange={(v) => set("require_order_number", v)}
        />
        <OptionRow
          label="Require Invoice Number"
          description="Operator must enter an Invoice Number against the incoming shipment"
          value={workflow.checkin.require_invoice_number}
          onChange={(v) => set("require_invoice_number", v)}
        />
        <OptionRow
          label="Require Picklist Number"
          description="Operator must provide a Picklist Number before accepting goods"
          value={workflow.checkin.require_picklist_number}
          onChange={(v) => set("require_picklist_number", v)}
        />
      </SectionCard>

      <SectionCard
        icon={<FiShield size={16} />}
        title="Chain Security & Logistics"
        description="Prevent unauthorized inventory from entering this facility"
        accent="#ef4444"
      >
        <OptionRow
          label="Strict Check-In: Only Allow If Dispatched Specifically For This Location"
          description="Ensures QR was officially checked out to this destination in the prior stage"
          value={workflow.checkin.only_if_checked_out}
          onChange={(v) => set("only_if_checked_out", v)}
        />
        <OptionRow
          label="Capture Transporter & Vehicle Details"
          description="Collect transporter company, driver contact, and vehicle plate number at the gate"
          value={workflow.checkin.allow_transporter_details}
          onChange={(v) => set("allow_transporter_details", v)}
        />
      </SectionCard>
    </div>
  );
};

const TabCheckout = ({ workflow, setWorkflow }) => {
  const setOrder = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      checkout: { ...prev.checkout, order: { ...prev.checkout.order, [key]: val } },
    }));

  const setCarton = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      checkout: { ...prev.checkout, carton: { ...prev.checkout.carton, [key]: val } },
    }));

  const setDirect = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      checkout: { ...prev.checkout, direct: { ...prev.checkout.direct, [key]: val } },
    }));

  const { order, carton, direct } = workflow.checkout;

  return (
    <div>
      <SectionCard
        icon={<FiPackage size={16} />}
        title="1. Order-Based Checkout"
        description="Structured dispatch based on customer/transfer orders"
        accent="#4f46e5"
      >
        <OptionRow
          label="Enable Order-Based Checkout"
          description="Allows warehouse operators to dispatch against orders"
          value={order.enabled}
          onChange={(v) => setOrder("enabled", v)}
        />
        {order.enabled && (
          <div style={{ paddingLeft: "12px", borderLeft: "2px solid #e0e7ff", marginTop: "10px" }}>
            <OptionRow
              label="Scan Against Order"
              description="Scan QR codes and match them against line items in the order"
              value={order.scan_against_order}
              onChange={(v) => setOrder("scan_against_order", v)}
            />
            <OptionRow
              label="Save Picklist Against Order"
              description="Bind and save a warehouse picklist to the order during checkout"
              value={order.save_picklist_against_order}
              onChange={(v) => setOrder("save_picklist_against_order", v)}
            />
            <OptionRow
              label="Enter Invoice Number Against Order"
              description="Capture tax invoice numbers associated with the outgoing order"
              value={order.enter_invoice_no}
              onChange={(v) => setOrder("enter_invoice_no", v)}
            />
            <OptionRow
              label="Checkout Through Invoice Number"
              description="Allow initiating the checkout scan using the invoice number"
              value={order.checkout_through_invoice}
              onChange={(v) => setOrder("checkout_through_invoice", v)}
            />
            <OptionRow
              label="Scan & Save Multiple Picklists Against Order"
              description="Consolidate multiple picklists into a single customer order dispatch"
              value={order.scan_multiple_picklists}
              onChange={(v) => setOrder("scan_multiple_picklists", v)}
            />
            <OptionRow
              label="Enable Partial Dispatch"
              description="Permit shipping a subset of ordered items and leaving remainder pending"
              value={order.enable_partial_dispatch}
              onChange={(v) => setOrder("enable_partial_dispatch", v)}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard
        icon={<FiBox size={16} />}
        title="2. Carton-Based Checkout"
        description="Bulk carton and outer packing box scanning workflows"
        accent="#f59e0b"
      >
        <OptionRow
          label="Enable Carton-Based Checkout"
          description="Scan master shipping cartons rather than opening inner units"
          value={carton.enabled}
          onChange={(v) => setCarton("enabled", v)}
        />
        {carton.enabled && (
          <div style={{ paddingLeft: "12px", borderLeft: "2px solid #fef3c7", marginTop: "10px" }}>
            <OptionRow
              label="Scan & Save Picklist"
              description="Attach a warehouse picklist during carton checkout"
              value={carton.scan_and_save_picklist}
              onChange={(v) => setCarton("scan_and_save_picklist", v)}
            />
            <OptionRow
              label="Scan & Save Multiple Cartons Against Picklist"
              description="Group multiple carton codes into a single picklist dispatch"
              value={carton.scan_multiple_cartons_against_picklist}
              onChange={(v) => setCarton("scan_multiple_cartons_against_picklist", v)}
            />
            <OptionRow
              label="Checkout Carton"
              description="Mark entire carton status as Dispatched immediately upon scan"
              value={carton.checkout_carton}
              onChange={(v) => setCarton("checkout_carton", v)}
            />
            <OptionRow
              label="Enable Partial Carton Dispatch"
              description="Permit dispatching some cartons of a picklist while holding others"
              value={carton.enable_partial_dispatch}
              onChange={(v) => setCarton("enable_partial_dispatch", v)}
            />
          </div>
        )}
      </SectionCard>

      <SectionCard
        icon={<FiZap size={16} />}
        title="3. Direct Fast-Track Checkout"
        description="Streamlined dispatch with minimal operator overhead"
        accent="#10b981"
      >
        <OptionRow
          label="Enable Direct Checkout"
          description="Allow dispatching QR codes straight away by providing an order number"
          value={direct.enabled}
          onChange={(v) => setDirect("enabled", v)}
        />
        {direct.enabled && (
          <div style={{ paddingLeft: "12px", borderLeft: "2px solid #d1fae5", marginTop: "10px" }}>
            <OptionRow
              label="Scan QR & Checkout Directly Against Order"
              description="Scan item QR → type order number → done. Skips picklist and carton steps."
              value={direct.enter_order_and_checkout_directly}
              onChange={(v) => setDirect("enter_order_and_checkout_directly", v)}
            />
          </div>
        )}
      </SectionCard>
    </div>
  );
};

const TabSync = ({ workflow, setWorkflow }) => {
  const setSync = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      sync: { ...prev.sync, [key]: val },
    }));

  return (
    <div>
      <SectionCard
        icon={<MdSync size={16} />}
        title="Enterprise Data Synchronization"
        description="Configure how document data flows between this stage and your external ERP (SAP, Oracle, Dynamics)"
        accent="#8b5cf6"
      >
        <SyncPicker
          label="📦 Order Data Synchronization"
          value={workflow.sync.order_sync}
          onChange={(v) => setSync("order_sync", v)}
        />
        <SyncPicker
          label="🗃️ Carton Information Sync"
          value={workflow.sync.carton_sync}
          onChange={(v) => setSync("carton_sync", v)}
        />
        <SyncPicker
          label="🧾 Invoicing Data Sync"
          value={workflow.sync.invoice_sync}
          onChange={(v) => setSync("invoice_sync", v)}
        />
        <SyncPicker
          label="📋 Picklist System Sync"
          value={workflow.sync.picklist_sync}
          onChange={(v) => setSync("picklist_sync", v)}
        />

        <div
          style={{
            marginTop: "16px",
            padding: "14px 18px",
            background: "#f0fdf4",
            borderRadius: "10px",
            border: "1px solid #bbf7d0",
            fontSize: "13px",
            color: "#166534",
            lineHeight: "1.5",
          }}
        >
          <strong>💡 Integration Guide:</strong>
          <br />• <strong>Manual:</strong> Operators type or upload CSV/manual documents within the portal.
          <br />• <strong>ERP:</strong> Real-time automated sync via webhooks/REST APIs from your central ERP.
        </div>
      </SectionCard>
    </div>
  );
};

const TabReturn = ({ formData, workflow, setWorkflow }) => {
  const set = (key, val) =>
    setWorkflow((prev) => ({
      ...prev,
      return: { ...prev.return, [key]: val },
    }));

  const isReturnEnabled = formData.return_allowed === 1;

  return (
    <div>
      {!isReturnEnabled && (
        <div
          style={{
            padding: "16px 20px",
            background: "#fef9c3",
            border: "1px solid #fde047",
            borderRadius: "12px",
            marginBottom: "20px",
            fontSize: "13.5px",
            color: "#854d0e",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FiAlertCircle size={20} style={{ flexShrink: 0 }} />
          <span>
            Return operations are currently <strong>disabled</strong> on this stage. To activate this workflow, enable{" "}
            <strong>&ldquo;Return Allowed&rdquo;</strong> in the <strong>Basic Settings</strong> tab.
          </span>
        </div>
      )}

      <SectionCard
        icon={<FiRepeat size={16} />}
        title="Return Inbound Channels"
        description="Documents and identifiers accepted to log returned merchandise"
        accent="#ef4444"
      >
        <OptionRow
          label="Return Through Order Number"
          description="Look up original sales/dispatch order and process item returns against it"
          value={workflow.return.return_through_order}
          onChange={(v) => set("return_through_order", v)}
        />
        <OptionRow
          label="Return Through Invoice Number"
          description="Verify original tax invoice before admitting returned stock"
          value={workflow.return.return_through_invoice}
          onChange={(v) => set("return_through_invoice", v)}
        />
        <OptionRow
          label="Return Through Picklist Number"
          description="Reverse dispatch against the picklist code"
          value={workflow.return.return_through_picklist}
          onChange={(v) => set("return_through_picklist", v)}
        />
        <OptionRow
          label="Return Through Carton Number"
          description="Accept and restock intact sealed cartons"
          value={workflow.return.return_through_carton}
          onChange={(v) => set("return_through_carton", v)}
        />
      </SectionCard>

      <SectionCard
        icon={<FiNavigation size={16} />}
        title="Return Path Restrictions"
        description="Control backward routing through the supply chain"
        accent="#8b5cf6"
      >
        <OptionRow
          label="Return to Immediate Upstream Location (N-1 Only)"
          description="Goods can only be returned back to the stage they came directly from"
          value={workflow.return.return_to_n_minus_1}
          onChange={(v) => set("return_to_n_minus_1", v)}
        />
        <OptionRow
          label="Return to Any Prior Location in the Chain"
          description="Items can be returned directly to any earlier stage (e.g. Depot directly back to Plant)"
          value={workflow.return.return_to_any_location}
          onChange={(v) => set("return_to_any_location", v)}
        />
      </SectionCard>

      <SectionCard
        icon={<FiLayers size={16} />}
        title="Granular Unit Returns"
        description="Handle broken packaging and individual items"
        accent="#10b981"
      >
        <OptionRow
          label="Allow Child SKU Return"
          description="Permits returning single unit QR codes without needing the master outer carton"
          value={workflow.return.allow_child_return}
          onChange={(v) => set("allow_child_return", v)}
        />
      </SectionCard>
    </div>
  );
};

// ─────────────────────────────────────────────────
// Interactive Graph Component
// ─────────────────────────────────────────────────
const WorkflowGraphView = ({ stages, onConfigureStage, onToggleStatus, onMoveOrder }) => {
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Map for easy ID lookups
  const stageMap = useMemo(() => {
    const map = {};
    stages.forEach((s) => {
      map[s.id] = s;
    });
    return map;
  }, [stages]);

  const selectedStage = stages.find((s) => s.id === selectedNodeId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Graph Toolbar / Legend */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "#4f46e512",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdAutoGraph size={20} />
          </div>
          <div>
            <div style={{ fontSize: "15px", fontWeight: 800, color: "#1e293b" }}>Supply Chain Pipeline Graph</div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              Visual map of linear flow stages, dispatch routing, and workflow policies
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#475569" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#10b981" }} />
            Check-In
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#475569" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#4f46e5" }} />
            Checkout
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#475569" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#8b5cf6" }} />
            ERP Sync
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#475569" }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#f59e0b" }} />
            Returns
          </div>
        </div>
      </div>

      {/* Visual Graph Canvas */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          padding: "32px 24px",
          backgroundImage: "radial-gradient(#cbd5e1 1.2px, transparent 1.2px)",
          backgroundSize: "24px 24px",
          overflowX: "auto",
          minHeight: "440px",
        }}
      >
        {stages.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <MdOutlineHub size={48} style={{ color: "#cbd5e1", marginBottom: "12px" }} />
            <div style={{ fontSize: "16px", fontWeight: 700, color: "#64748b" }}>No stages in the pipeline yet</div>
            <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
              Click &quot;Add Stage&quot; above to configure your first facility node.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", minWidth: "fit-content", paddingBottom: "16px" }}>
            {stages.map((stage, idx) => {
              const isSelected = selectedNodeId === stage.id;
              const wf = stage.workflow_config || defaultWorkflowConfig;
              const allowedDestinations = Array.isArray(stage.allowed_checkout_types)
                ? stage.allowed_checkout_types
                : [];

              return (
                <React.Fragment key={stage.id}>
                  {/* Node Card */}
                  <div
                    onClick={() => setSelectedNodeId(isSelected ? null : stage.id)}
                    style={{
                      width: "310px",
                      background: "#ffffff",
                      borderRadius: "14px",
                      border: isSelected ? "2.5px solid #4f46e5" : "1.5px solid #e2e8f0",
                      boxShadow: isSelected
                        ? "0 12px 30px rgba(79,70,229,0.18)"
                        : "0 4px 16px rgba(15,23,42,0.04)",
                      overflow: "hidden",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    {/* Node Header */}
                    <div
                      style={{
                        padding: "16px",
                        background:
                          stage.status === "1"
                            ? isSelected
                              ? "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)"
                              : "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)"
                            : "linear-gradient(135deg, #64748b 0%, #475569 100%)",
                        color: "#fff",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "6px",
                              background: "rgba(255,255,255,0.2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: 800,
                            }}
                          >
                            #{stage.order_index}
                          </span>
                          <span style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#c7d2fe" }}>
                            Stage {idx + 1}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: stage.status === "1" ? "#10b98133" : "#ef444433",
                            color: stage.status === "1" ? "#6ee7b7" : "#fca5a5",
                            border: `1px solid ${stage.status === "1" ? "#10b98166" : "#ef444466"}`,
                          }}
                        >
                          {stage.status === "1" ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "10px",
                            background: "rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            flexShrink: 0,
                          }}
                        >
                          {getStageIcon(stage.location_type)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "16px", fontWeight: 800, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {stage.location_type}
                          </div>
                          {stage.location_type_code && (
                            <span style={{ fontSize: "11px", color: "#c7d2fe", fontWeight: 600 }}>
                              Code: {stage.location_type_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Node Body Details */}
                    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                      {/* Capabilities Matrix */}
                      <div>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                          Operations Allowed
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <span
                            style={{
                              flex: 1,
                              textAlign: "center",
                              fontSize: "11px",
                              fontWeight: 700,
                              padding: "4px 0",
                              borderRadius: "6px",
                              background: stage.checkin_allowed === 1 ? "#d1fae5" : "#f1f5f9",
                              color: stage.checkin_allowed === 1 ? "#059669" : "#94a3b8",
                            }}
                          >
                            Check-In {stage.checkin_allowed === 1 ? "✓" : "✗"}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              textAlign: "center",
                              fontSize: "11px",
                              fontWeight: 700,
                              padding: "4px 0",
                              borderRadius: "6px",
                              background: stage.checkout_allowed === 1 ? "#e0e7ff" : "#f1f5f9",
                              color: stage.checkout_allowed === 1 ? "#4338ca" : "#94a3b8",
                            }}
                          >
                            Checkout {stage.checkout_allowed === 1 ? "✓" : "✗"}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              textAlign: "center",
                              fontSize: "11px",
                              fontWeight: 700,
                              padding: "4px 0",
                              borderRadius: "6px",
                              background: stage.return_allowed === 1 ? "#fef3c7" : "#f1f5f9",
                              color: stage.return_allowed === 1 ? "#b45309" : "#94a3b8",
                            }}
                          >
                            Return {stage.return_allowed === 1 ? "✓" : "✗"}
                          </span>
                        </div>
                      </div>

                      {/* QR Packaging Privileges */}
                      {(stage.qr_mapping_allowed === 1 || stage.qr_demapping_allowed === 1) && (
                        <div>
                          <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                            QR Hierarchy Privileges
                          </div>
                          <div style={{ display: "flex", gap: "6px" }}>
                            {stage.qr_mapping_allowed === 1 && (
                              <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd" }}>
                                🔗 Mapping
                              </span>
                            )}
                            {stage.qr_demapping_allowed === 1 && (
                              <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#fce7f3", color: "#be185d", border: "1px solid #fbcfe8" }}>
                                ✂️ Demapping
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Active Dispatch Flows */}
                      <div>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                          Active Dispatch Modes
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {wf.checkout?.order?.enabled && (
                            <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe" }}>
                              📦 Order-Based
                            </span>
                          )}
                          {wf.checkout?.carton?.enabled && (
                            <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" }}>
                              🗃️ Carton-Based
                            </span>
                          )}
                          {wf.checkout?.direct?.enabled && (
                            <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" }}>
                              ⚡ Direct
                            </span>
                          )}
                          {!wf.checkout?.order?.enabled && !wf.checkout?.carton?.enabled && !wf.checkout?.direct?.enabled && (
                            <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>
                              No dispatch modes configured
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ERP Sync Modes */}
                      <div>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                          Data Synchronization
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {["order_sync", "invoice_sync", "carton_sync", "picklist_sync"].map((syncKey) => {
                            const mode = wf.sync?.[syncKey] || "manual";
                            const cleanName = syncKey.replace("_sync", "");
                            return (
                              <span
                                key={syncKey}
                                style={{
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  background: mode === "erp" ? "#f3e8ff" : "#f1f5f9",
                                  color: mode === "erp" ? "#7e22ce" : "#64748b",
                                  border: `1px solid ${mode === "erp" ? "#d8b4fe" : "#e2e8f0"}`,
                                  textTransform: "capitalize",
                                }}
                              >
                                {cleanName}: {mode}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Destinations */}
                      {allowedDestinations.length > 0 && (
                        <div>
                          <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                            Outgoing Routes (To)
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {allowedDestinations.map((destId) => {
                              const target = stageMap[destId];
                              return (
                                <span
                                  key={destId}
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    background: "#f0fdf4",
                                    color: "#15803d",
                                    border: "1px solid #bbf7d0",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  → {target?.location_type || `#${destId}`}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Node Actions Button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onConfigureStage(stage);
                        }}
                        style={{
                          marginTop: "6px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          background: "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)",
                          color: "#fff",
                          fontSize: "12.5px",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          boxShadow: "0 2px 8px rgba(79,70,229,0.25)",
                        }}
                      >
                        <FiSliders size={14} /> Configure Workflow
                      </button>
                    </div>
                  </div>

                  {/* Transition Connector Arrow */}
                  {idx < stages.length - 1 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        paddingTop: "60px",
                        color: "#94a3b8",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "#fff",
                          border: "1.5px solid #cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#4f46e5",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                        }}
                      >
                        <FiArrowRight size={18} />
                      </div>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          marginTop: "6px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Next
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Node Deep Inspector */}
      {selectedStage && (
        <div
          style={{
            background: "#fff",
            borderRadius: "14px",
            border: "1.5px solid #c7d2fe",
            padding: "20px 24px",
            boxShadow: "0 4px 16px rgba(79,70,229,0.08)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  background: "#4f46e515",
                  color: "#4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {getStageIcon(selectedStage.location_type)}
              </div>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>
                  {selectedStage.location_type} · Detailed Workflow Inspection
                </div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>
                  Order #{selectedStage.order_index} · Geofence: {selectedStage.geofence_radius || 500}m · Capacity:{" "}
                  {selectedStage.max_capacity || "Unlimited"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onConfigureStage(selectedStage)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: "linear-gradient(135deg, #4f46e5, #6366f1)",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FiEdit2 size={13} /> Edit Full Stage Settings
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#0284c7", marginBottom: "6px" }}>
                🔗 QR Mapping Rules
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                <li>Mapping Allowed: {selectedStage.qr_mapping_allowed === 1 ? "Yes" : "No"}</li>
                <li>Strategy: {selectedStage.workflow_config?.mapping?.mapping_type === "attribute_based" ? "Attribute-Based" : "Direct (Product Code)"}</li>
                <li>By Size: {selectedStage.workflow_config?.mapping?.by_size ? "Enforced" : "No"}</li>
                <li>By Quantity: {selectedStage.workflow_config?.mapping?.by_qty ? "Enforced" : "No"}</li>
                <li>By Color: {selectedStage.workflow_config?.mapping?.by_color ? "Enforced" : "No"}</li>
              </ul>
            </div>

            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#059669", marginBottom: "6px" }}>
                📥 Inbound Check-In Rules
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                <li>Parent Check-In: {selectedStage.workflow_config?.checkin?.through_parent ? "Enabled" : "Disabled"}</li>
                <li>Child Check-In: {selectedStage.workflow_config?.checkin?.through_child ? "Enabled" : "Disabled"}</li>
                <li>Require Order: {selectedStage.workflow_config?.checkin?.require_order_number ? "Yes" : "No"}</li>
                <li>Require Invoice: {selectedStage.workflow_config?.checkin?.require_invoice_number ? "Yes" : "No"}</li>
                <li>Only If Checked Out: {selectedStage.workflow_config?.checkin?.only_if_checked_out ? "Strict" : "No"}</li>
              </ul>
            </div>

            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#4f46e5", marginBottom: "6px" }}>
                📤 Outbound Checkout Rules
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                <li>Order Flow: {selectedStage.workflow_config?.checkout?.order?.enabled ? "Enabled" : "Disabled"}</li>
                <li>Carton Flow: {selectedStage.workflow_config?.checkout?.carton?.enabled ? "Enabled" : "Disabled"}</li>
                <li>Direct Dispatch: {selectedStage.workflow_config?.checkout?.direct?.enabled ? "Enabled" : "Disabled"}</li>
                <li>Partial Dispatch: {selectedStage.workflow_config?.checkout?.order?.enable_partial_dispatch ? "Allowed" : "Blocked"}</li>
              </ul>
            </div>

            <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#b45309", marginBottom: "6px" }}>
                🔄 Return & Reverse Logistics
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                <li>Returns Allowed: {selectedStage.return_allowed === 1 ? "Active" : "Disabled"}</li>
                <li>Return Channel: {selectedStage.workflow_config?.return?.return_through_order ? "Via Order" : "Standard"}</li>
                <li>Routing: {selectedStage.workflow_config?.return?.return_to_n_minus_1 ? "N-1 Only" : "Any Location"}</li>
                <li>Child Return: {selectedStage.workflow_config?.return?.allow_child_return ? "Permitted" : "Blocked"}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────
const SupplyBeamConfigManager = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const token = sessionStorage.getItem("access_token");

  const tenantInfo = routeLocation?.state ?? null;

  useEffect(() => {
    if (!tenantInfo?.tenant_id) {
      toast.error("No client selected. Please select a client first.");
      navigate("/supplyBeamLocations");
    }
  }, [tenantInfo]);

  // Main page view states: "overview" (Graph / Table / Theme) vs "config" (Full page editor)
  const [viewMode, setViewMode] = useState("overview"); // "overview" | "config"
  const [overviewTab, setOverviewTab] = useState(routeLocation?.state?.initialTab || "graph"); // "graph" | "table" | "theme"

  const [stages, setStages] = useState([]);
  const [activeConfigTab, setActiveConfigTab] = useState("basic");
  const [formData, setFormData] = useState(emptyForm);
  const [search, setSearch] = useState("");

  const setWorkflow = (updater) => {
    setFormData((prev) => {
      const currentWf = prev.workflow_config
        ? JSON.parse(JSON.stringify(prev.workflow_config))
        : JSON.parse(JSON.stringify(defaultWorkflowConfig));
      return {
        ...prev,
        workflow_config:
          typeof updater === "function" ? updater(currentWf) : updater,
      };
    });
  };

  const isTabDisabled = (tabId) => {
    if (tabId === "mapping") return Number(formData.qr_mapping_allowed) !== 1;
    if (tabId === "checkin") return Number(formData.checkin_allowed) !== 1;
    if (tabId === "checkout") return Number(formData.checkout_allowed) !== 1;
    if (tabId === "return") return Number(formData.return_allowed) !== 1;
    return false;
  };

  const enabledTabs = useMemo(() => {
    return WORKFLOW_TABS.filter((tab) => !isTabDisabled(tab.id));
  }, [formData.qr_mapping_allowed, formData.checkin_allowed, formData.checkout_allowed, formData.return_allowed]);

  useEffect(() => {
    if (isTabDisabled(activeConfigTab)) {
      setActiveConfigTab("basic");
    }
  }, [formData.qr_mapping_allowed, formData.checkin_allowed, formData.checkout_allowed, formData.return_allowed]);

  const [fetchConfigs, { data: configsData, isLoading: configsLoading, error: configsError }] =
    useAdminGetSupplyBeamConfigListMutation();
  const [createConfig, { data: createData, isLoading: creating, error: createError }] =
    useAdminCreateSupplyBeamConfigMutation();
  const [updateConfig, { data: updateData, isLoading: updating, error: updateError }] =
    useAdminUpdateSupplyBeamConfigMutation();
  const [toggleStatus, { data: toggleData, error: toggleError }] =
    useAdminToggleSupplyBeamConfigStatusMutation();
  const [reorderConfigs, { data: reorderData, error: reorderError }] =
    useAdminReorderSupplyBeamConfigMutation();

  const loadData = () => {
    if (!tenantInfo?.tenant_id) return;
    fetchConfigs({
      token,
      tenant_id: tenantInfo.tenant_id,
      query: search ? `search=${encodeURIComponent(search)}` : "",
    });
  };

  useEffect(() => {
    loadData();
  }, [search]);

  useMemo(() => {
    if (configsData) setStages(configsData.body ?? []);
    if (configsError) toast.error(configsError?.data?.message || "Failed to load configurations", { id: "configErr" });
  }, [configsData, configsError]);

  useMemo(() => {
    if (createData) {
      toast.success(createData.message || "Stage created successfully", { id: "cOk" });
      setViewMode("overview");
      setFormData(emptyForm);
      loadData();
    }
    if (createError) toast.error(createError?.data?.message || "Failed to create stage", { id: "cErr" });
  }, [createData, createError]);

  useMemo(() => {
    if (updateData) {
      toast.success(updateData.message || "Stage updated successfully", { id: "uOk" });
      setViewMode("overview");
      setFormData(emptyForm);
      loadData();
    }
    if (updateError) toast.error(updateError?.data?.message || "Failed to update stage", { id: "uErr" });
  }, [updateData, updateError]);

  useMemo(() => {
    if (toggleData) {
      toast.success(toggleData.message || "Status updated", { id: "tOk" });
      loadData();
    }
    if (toggleError) toast.error(toggleError?.data?.message || "Failed to toggle status", { id: "tErr" });
  }, [toggleData, toggleError]);

  useMemo(() => {
    if (reorderData) {
      toast.success("Stage order updated successfully", { id: "rOk" });
      loadData();
    }
    if (reorderError) toast.error(reorderError?.data?.message || "Failed to reorder stages", { id: "rErr" });
  }, [reorderData, reorderError]);

  // Open Full-View Editor for Create
  const handleAddNew = () => {
    const nextOrder = stages.length > 0 ? Math.max(...stages.map((s) => s.order_index || 0)) + 1 : 1;
    setFormData({ ...JSON.parse(JSON.stringify(emptyForm)), order_index: nextOrder });
    setActiveConfigTab("basic");
    setViewMode("config");
  };

  // Open Full-View Editor for Edit
  const handleConfigureStage = (stage) => {
    let parsedWf = {};
    if (stage.workflow_config && typeof stage.workflow_config === "object") {
      try {
        parsedWf = JSON.parse(JSON.stringify(stage.workflow_config));
      } catch {
        parsedWf = {};
      }
    } else if (typeof stage.workflow_config === "string") {
      try {
        parsedWf = JSON.parse(stage.workflow_config);
      } catch {
        parsedWf = {};
      }
    }

    const defaultWf = JSON.parse(JSON.stringify(defaultWorkflowConfig));
    const wf = {
      ...defaultWf,
      ...parsedWf,
      mapping: {
        ...defaultWf.mapping,
        ...(parsedWf.mapping || {}),
      },
      checkin: {
        ...defaultWf.checkin,
        ...(parsedWf.checkin || {}),
      },
      checkout: {
        ...defaultWf.checkout,
        ...(parsedWf.checkout || {}),
        order: {
          ...defaultWf.checkout.order,
          ...(parsedWf.checkout?.order || {}),
        },
        carton: {
          ...defaultWf.checkout.carton,
          ...(parsedWf.checkout?.carton || {}),
        },
        direct: {
          ...defaultWf.checkout.direct,
          ...(parsedWf.checkout?.direct || {}),
        },
      },
      sync: {
        ...defaultWf.sync,
        ...(parsedWf.sync || {}),
      },
      return: {
        ...defaultWf.return,
        ...(parsedWf.return || {}),
      },
    };

    setFormData({
      id: stage.id,
      location_type: stage.location_type || "",
      location_type_code: stage.location_type_code || "",
      order_index: stage.order_index ?? 1,
      checkin_allowed: stage.checkin_allowed ?? 1,
      checkout_allowed: stage.checkout_allowed ?? 1,
      return_allowed: stage.return_allowed ?? 0,
      qr_mapping_allowed: stage.qr_mapping_allowed ?? 0,
      qr_demapping_allowed: stage.qr_demapping_allowed ?? 0,
      geofence_radius: stage.geofence_radius ?? 500,
      max_capacity:
        stage.max_capacity !== null && stage.max_capacity !== undefined ? stage.max_capacity : "",
      allowed_checkout_types: Array.isArray(stage.allowed_checkout_types)
        ? [...stage.allowed_checkout_types]
        : [],
      workflow_config: wf,
      status: stage.status ?? "1",
    });
    setActiveConfigTab("basic");
    setViewMode("config");
  };

  const handleToggleStatus = (stage) => {
    const nextStatus = stage.status === "1" ? "0" : "1";
    toggleStatus({ token, tenant_id: tenantInfo.tenant_id, id: stage.id, status: nextStatus });
  };

  const handleMoveOrder = (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= stages.length) return;
    const updated = [...stages];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;
    const items = updated.map((item, idx) => ({ id: item.id, order_index: idx + 1 }));
    reorderConfigs({ token, tenant_id: tenantInfo.tenant_id, items });
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.location_type.trim()) {
      toast.error("Location stage name is required");
      return;
    }

    const payload = {
      location_type: formData.location_type.trim(),
      location_type_code: formData.location_type_code
        ? formData.location_type_code.trim().toUpperCase()
        : null,
      order_index: Number(formData.order_index) || 1,
      checkin_allowed: Number(formData.checkin_allowed),
      checkout_allowed: Number(formData.checkout_allowed),
      return_allowed: Number(formData.return_allowed),
      qr_mapping_allowed: Number(formData.qr_mapping_allowed),
      qr_demapping_allowed: Number(formData.qr_demapping_allowed),
      geofence_radius: formData.geofence_radius !== "" ? Number(formData.geofence_radius) : 500,
      max_capacity: formData.max_capacity !== "" ? Number(formData.max_capacity) : null,
      allowed_checkout_types: formData.allowed_checkout_types,
      workflow_config: formData.workflow_config,
      status: formData.status,
    };

    if (formData.id) {
      updateConfig({ token, tenant_id: tenantInfo.tenant_id, id: formData.id, body: payload });
    } else {
      createConfig({ token, tenant_id: tenantInfo.tenant_id, body: payload });
    }
  };

  const stageMap = useMemo(() => {
    const map = {};
    stages.forEach((s) => {
      map[s.id] = s.location_type;
    });
    return map;
  }, [stages]);

  const stats = useMemo(() => {
    const total = stages.length;
    const active = stages.filter((s) => s.status === "1").length;
    const inbound = stages.filter((s) => s.checkin_allowed === 1).length;
    const outbound = stages.filter((s) => s.checkout_allowed === 1).length;
    const returns = stages.filter((s) => s.return_allowed === 1).length;
    return { total, active, inbound, outbound, returns };
  }, [stages]);

  const isSubmitting = creating || updating;

  // Table Columns
  const tableColumns = [
    {
      name: "Order",
      width: "110px",
      cell: (row, idx) => (
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: "linear-gradient(135deg,#4f46e5,#3730a3)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "800",
              flexShrink: 0,
            }}
          >
            {row.order_index}
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <button
              onClick={() => handleMoveOrder(idx, "up")}
              disabled={idx === 0}
              style={{
                background: "none",
                border: "none",
                padding: "1px",
                cursor: idx === 0 ? "not-allowed" : "pointer",
                color: idx === 0 ? "#cbd5e1" : "#4f46e5",
                lineHeight: 1,
              }}
            >
              <FiArrowUp size={13} />
            </button>
            <button
              onClick={() => handleMoveOrder(idx, "down")}
              disabled={idx === stages.length - 1}
              style={{
                background: "none",
                border: "none",
                padding: "1px",
                cursor: idx === stages.length - 1 ? "not-allowed" : "pointer",
                color: idx === stages.length - 1 ? "#cbd5e1" : "#4f46e5",
                lineHeight: 1,
              }}
            >
              <FiArrowDown size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      name: "Stage",
      grow: 2,
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "10px",
              background: "linear-gradient(135deg,#f0f4ff,#e0e7ff)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#4f46e5",
              flexShrink: 0,
            }}
          >
            {getStageIcon(row.location_type)}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>{row.location_type}</div>
            {row.location_type_code && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  background: "#f1f5f9",
                  color: "#64748b",
                  padding: "1px 8px",
                  borderRadius: "4px",
                }}
              >
                {row.location_type_code}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      name: "Capabilities",
      grow: 2,
      cell: (row) => (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {[
            { label: "Check-In", enabled: row.checkin_allowed === 1, color: "#10b981" },
            { label: "Checkout", enabled: row.checkout_allowed === 1, color: "#4f46e5" },
            { label: "Return", enabled: row.return_allowed === 1, color: "#f59e0b" },
            { label: "QR Map", enabled: row.qr_mapping_allowed === 1, color: "#06b6d4" },
            { label: "QR Demap", enabled: row.qr_demapping_allowed === 1, color: "#ec4899" },
          ].map((cap) => (
            <span
              key={cap.label}
              style={{
                fontSize: "11px",
                fontWeight: 700,
                padding: "3px 9px",
                borderRadius: "6px",
                background: cap.enabled ? `${cap.color}15` : "#f1f5f9",
                color: cap.enabled ? cap.color : "#94a3b8",
                border: `1px solid ${cap.enabled ? `${cap.color}30` : "#e2e8f0"}`,
              }}
            >
              {cap.label}
            </span>
          ))}
        </div>
      ),
    },
    {
      name: "Mapping Criteria",
      grow: 2,
      cell: (row) => {
        if (row.qr_mapping_allowed !== 1) {
          return (
            <span style={{ fontSize: "11px", color: "#94a3b8", fontStyle: "italic" }}>
              Disabled
            </span>
          );
        }
        const mapping = row.workflow_config?.mapping;
        const isDirect = mapping?.mapping_type === "direct" || (!mapping?.mapping_type && mapping?.direct_product_code !== false);
        const attrs = [];
        if (mapping?.by_size) attrs.push("Size");
        if (mapping?.by_qty) attrs.push("Qty");
        if (mapping?.by_color) attrs.push("Color");

        return (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", alignItems: "center" }}>
            {isDirect && (
              <span
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  padding: "2px 7px",
                  borderRadius: "5px",
                  background: "#0284c715",
                  color: "#0284c7",
                  border: "1px solid #0284c730",
                }}
              >
                Direct (SKU)
              </span>
            )}
            {attrs.map((attr) => (
              <span
                key={attr}
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  padding: "2px 7px",
                  borderRadius: "5px",
                  background: "#8b5cf615",
                  color: "#7c3aed",
                  border: "1px solid #8b5cf630",
                }}
              >
                {attr}
              </span>
            ))}
            {!isDirect && attrs.length === 0 && (
              <span
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  padding: "2px 7px",
                  borderRadius: "5px",
                  background: "#0284c715",
                  color: "#0284c7",
                  border: "1px solid #0284c730",
                }}
              >
                Direct (SKU)
              </span>
            )}
          </div>
        );
      },
    },
    {
      name: "Workflow Rules",
      grow: 2,
      cell: (row) => {
        const wf = row.workflow_config;
        const modes = [];
        if (wf?.checkout?.order?.enabled) modes.push("Order");
        if (wf?.checkout?.carton?.enabled) modes.push("Carton");
        if (wf?.checkout?.direct?.enabled) modes.push("Direct");
        return modes.length > 0 ? (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {modes.map((m) => (
              <span
                key={m}
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  padding: "3px 8px",
                  borderRadius: "6px",
                  background: "#4f46e510",
                  color: "#4f46e5",
                  border: "1px solid #4f46e530",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic" }}>Default</span>
        );
      },
    },
    {
      name: "Destinations",
      grow: 2,
      cell: (row) => {
        const dests = Array.isArray(row.allowed_checkout_types) ? row.allowed_checkout_types : [];
        return dests.length > 0 ? (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {dests.slice(0, 3).map((id) => (
              <span
                key={id}
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: "6px",
                  background: "#f0fdf4",
                  color: "#10b981",
                  border: "1px solid #bbf7d0",
                }}
              >
                → {stageMap[id] || `#${id}`}
              </span>
            ))}
            {dests.length > 3 && (
              <span style={{ fontSize: "10px", color: "#94a3b8" }}>+{dests.length - 3}</span>
            )}
          </div>
        ) : (
          <span style={{ fontSize: "12px", color: "#cbd5e1" }}>—</span>
        );
      },
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => (
        <button
          onClick={() => handleToggleStatus(row)}
          type="button"
          style={{
            padding: "5px 14px",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "11.5px",
            fontWeight: 700,
            background: row.status === "1" ? "#f0fdf4" : "#fef2f2",
            color: row.status === "1" ? "#10b981" : "#ef4444",
            border: `1px solid ${row.status === "1" ? "#bbf7d0" : "#fecaca"}`,
          }}
        >
          {row.status === "1" ? "Active" : "Inactive"}
        </button>
      ),
    },
    {
      name: "Action",
      width: "140px",
      cell: (row) => (
        <button
          onClick={() => handleConfigureStage(row)}
          type="button"
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            border: "1px solid #c7d2fe",
            background: "#f0f4ff",
            color: "#4f46e5",
            fontSize: "12px",
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <FiSliders size={13} /> Configure
        </button>
      ),
    },
  ];

  // ══════════════════════════════════════════════════════
  // VIEW MODE: FULL-VIEW CONFIGURATION PAGE
  // ══════════════════════════════════════════════════════
  if (viewMode === "config") {
    const isEditMode = Boolean(formData.id);

    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', sans-serif" }}>
        {/* Top Navbar */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
            padding: "18px 32px",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button
                type="button"
                onClick={() => setViewMode("overview")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "8px",
                  color: "#c7d2fe",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  padding: "8px 14px",
                  cursor: "pointer",
                }}
              >
                <FiArrowLeft size={15} /> Back to Overview
              </button>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ color: "#a5b4fc", fontSize: "12px", fontWeight: 600 }}>
                    {tenantInfo?.company_name || tenantInfo?.name || "Client"}
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.3)" }}>/</span>
                  <span style={{ color: "#fff", fontSize: "12px", fontWeight: 700 }}>
                    {isEditMode ? formData.location_type || "Stage" : "New Stage"}
                  </span>
                </div>
                <h1 style={{ color: "#fff", fontSize: "20px", fontWeight: 800, margin: "2px 0 0" }}>
                  {isEditMode ? `Configure ${formData.location_type} Workflow` : "Add New Supply Chain Stage"}
                </h1>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                type="button"
                onClick={() => setViewMode("overview")}
                style={{
                  padding: "9px 18px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "transparent",
                  color: "#c7d2fe",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "9px 24px",
                  borderRadius: "8px",
                  border: "none",
                  background: isSubmitting ? "#94a3b8" : "linear-gradient(135deg,#10b981,#059669)",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
                }}
              >
                {isSubmitting ? (
                  <>
                    <FiRefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Saving...
                  </>
                ) : (
                  <>
                    <FiSave size={15} /> {isEditMode ? "Save Changes" : "Create Stage"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Full View Content Canvas */}
        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "28px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: isEditMode ? "280px 1fr 340px" : "1fr 360px", gap: "28px", alignItems: "start" }}>
            {/* Left Nav (Only shown in Edit mode with all tabs) */}
            {isEditMode ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #e2e8f0",
                  padding: "12px",
                  position: "sticky",
                  top: "92px",
                }}
              >
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.6px", padding: "8px 12px 12px" }}>
                  Configuration Sections
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {WORKFLOW_TABS.map((tab) => {
                    const isDisabled = isTabDisabled(tab.id);
                    const isActive = activeConfigTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          setActiveConfigTab(tab.id);
                        }}
                        title={
                          isDisabled
                            ? `${tab.label} is disabled because this capability is turned off in Basic Settings`
                            : tab.label
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 14px",
                          borderRadius: "10px",
                          border: isDisabled ? "1px dashed #e2e8f0" : "none",
                          background: isDisabled
                            ? "#f1f5f9"
                            : isActive
                            ? "linear-gradient(135deg,#4f46e5,#6366f1)"
                            : "transparent",
                          color: isDisabled ? "#94a3b8" : isActive ? "#fff" : "#475569",
                          fontSize: "13.5px",
                          fontWeight: isActive ? 700 : 600,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          textAlign: "left",
                          transition: "all 0.15s ease",
                          opacity: isDisabled ? 0.65 : 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ color: isDisabled ? "#94a3b8" : isActive ? "#fff" : "#4f46e5" }}>
                            {tab.icon}
                          </span>
                          <span>{tab.label}</span>
                        </div>
                        {isDisabled && (
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: "4px",
                              background: "#e2e8f0",
                              color: "#64748b",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Disabled
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* Main Form Area */}
            <div>
              {(!isEditMode || activeConfigTab === "basic") && (
                <TabBasicInfo formData={formData} setFormData={setFormData} stages={stages} />
              )}
              {isEditMode && activeConfigTab === "mapping" && !isTabDisabled("mapping") && (
                <TabMapping formData={formData} workflow={formData.workflow_config} setWorkflow={setWorkflow} />
              )}
              {isEditMode && activeConfigTab === "checkin" && !isTabDisabled("checkin") && (
                <TabCheckin workflow={formData.workflow_config} setWorkflow={setWorkflow} />
              )}
              {isEditMode && activeConfigTab === "checkout" && !isTabDisabled("checkout") && (
                <TabCheckout workflow={formData.workflow_config} setWorkflow={setWorkflow} />
              )}
              {isEditMode && activeConfigTab === "sync" && (
                <TabSync workflow={formData.workflow_config} setWorkflow={setWorkflow} />
              )}
              {isEditMode && activeConfigTab === "return" && !isTabDisabled("return") && (
                <TabReturn formData={formData} workflow={formData.workflow_config} setWorkflow={setWorkflow} />
              )}

              {/* Bottom Navigation Buttons */}
              {isEditMode && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: "20px",
                    borderTop: "1px solid #e2e8f0",
                    marginTop: "20px",
                  }}
                >
                  {enabledTabs.findIndex((t) => t.id === activeConfigTab) > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        const idx = enabledTabs.findIndex((t) => t.id === activeConfigTab);
                        setActiveConfigTab(enabledTabs[idx - 1].id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "10px 18px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        background: "#fff",
                        color: "#475569",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      <FiArrowLeft size={14} /> Previous: {enabledTabs[enabledTabs.findIndex((t) => t.id === activeConfigTab) - 1]?.label}
                    </button>
                  ) : <div />}

                  {enabledTabs.findIndex((t) => t.id === activeConfigTab) < enabledTabs.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => {
                        const idx = enabledTabs.findIndex((t) => t.id === activeConfigTab);
                        setActiveConfigTab(enabledTabs[idx + 1].id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border: "none",
                        background: "linear-gradient(135deg,#4f46e5,#6366f1)",
                        color: "#fff",
                        fontSize: "13px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Next: {enabledTabs[enabledTabs.findIndex((t) => t.id === activeConfigTab) + 1]?.label} <FiArrowRight size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "10px 24px",
                        borderRadius: "8px",
                        border: "none",
                        background: "linear-gradient(135deg,#10b981,#059669)",
                        color: "#fff",
                        fontSize: "13px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      <FiSave size={14} /> Save Configuration
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right Live Stage Summary Card */}
            <div
              style={{
                background: "#fff",
                borderRadius: "14px",
                border: "1.5px solid #e2e8f0",
                padding: "20px",
                position: "sticky",
                top: "92px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: "#4f46e512",
                    color: "#4f46e5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {getStageIcon(formData.location_type)}
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 800, color: "#1e293b" }}>
                    {formData.location_type || "Untitled Stage"}
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Live Pipeline Preview</div>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "12.5px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Order Sequence</span>
                  <span style={{ fontWeight: 700, color: "#1e293b" }}>#{formData.order_index}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Check-In Enabled</span>
                  <span style={{ fontWeight: 700, color: formData.checkin_allowed ? "#059669" : "#dc2626" }}>
                    {formData.checkin_allowed ? "Yes" : "No"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Checkout Enabled</span>
                  <span style={{ fontWeight: 700, color: formData.checkout_allowed ? "#4f46e5" : "#dc2626" }}>
                    {formData.checkout_allowed ? "Yes" : "No"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Returns Allowed</span>
                  <span style={{ fontWeight: 700, color: formData.return_allowed ? "#d97706" : "#64748b" }}>
                    {formData.return_allowed ? "Yes" : "No"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>QR Mapping</span>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontWeight: 700, color: formData.qr_mapping_allowed ? "#0284c7" : "#64748b" }}>
                      {formData.qr_mapping_allowed ? "Allowed" : "Disabled"}
                    </span>
                    {Boolean(formData.qr_mapping_allowed) && (
                      <div style={{ fontSize: "10.5px", color: "#64748b", marginTop: "2px" }}>
                        {formData.workflow_config?.mapping?.mapping_type === "attribute_based"
                          ? `Attr: ${[
                              formData.workflow_config?.mapping?.by_size && "Size",
                              formData.workflow_config?.mapping?.by_qty && "Qty",
                              formData.workflow_config?.mapping?.by_color && "Color",
                            ]
                              .filter(Boolean)
                              .join(", ") || "Custom"}`
                          : "Direct (product_code)"}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>QR Demapping</span>
                  <span style={{ fontWeight: 700, color: formData.qr_demapping_allowed ? "#db2777" : "#64748b" }}>
                    {formData.qr_demapping_allowed ? "Allowed" : "Disabled"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <span style={{ color: "#64748b" }}>Geofence</span>
                  <span style={{ fontWeight: 700, color: "#1e293b" }}>{formData.geofence_radius || 500} meters</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                  <span style={{ color: "#64748b" }}>Max Capacity</span>
                  <span style={{ fontWeight: 700, color: "#1e293b" }}>{formData.max_capacity || "Unlimited"}</span>
                </div>
              </div>

              {!isEditMode && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    borderRadius: "8px",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    fontSize: "12px",
                    color: "#1e40af",
                    lineHeight: "1.4",
                  }}
                >
                  💡 <strong>Note:</strong> Once this stage is created, you will be able to configure Check-In, Checkout, Sync, and Return flow rules.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════
  // VIEW MODE: OVERVIEW SCREEN (GRAPH & TABLE)
  // ══════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', sans-serif" }}>
      {/* Hero Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
          padding: "24px 32px 30px",
        }}
      >
        <button
          onClick={() => navigate("/supplyBeamLocations")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            color: "#c7d2fe",
            fontSize: "12px",
            fontWeight: 600,
            padding: "6px 12px",
            cursor: "pointer",
            marginBottom: "16px",
          }}
        >
          <FiArrowLeft size={14} /> Back to Clients
        </button>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "12px",
                  background: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#a5b4fc",
                }}
              >
                <MdOutlineLinearScale size={24} />
              </div>
              <div>
                <h1 style={{ color: "#fff", fontSize: "22px", fontWeight: 800, margin: 0 }}>
                  Supply Chain Workflow Topology
                </h1>
                <p style={{ color: "#a5b4fc", fontSize: "13px", margin: "2px 0 0" }}>
                  {tenantInfo?.company_name || tenantInfo?.name || "Client"} · End-to-End Tracking & Dispatch Pipeline
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleAddNew}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "linear-gradient(135deg,#818cf8,#6366f1)",
              border: "none",
              borderRadius: "10px",
              color: "#fff",
              padding: "11px 22px",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
            }}
          >
            <FiPlus size={16} /> Add Stage
          </button>
        </div>

        {/* KPI Stats Bar */}
        <div style={{ display: "flex", gap: "12px", marginTop: "24px", flexWrap: "wrap" }}>
          {[
            { label: "Total Stages", value: stats.total, icon: <FiLayers size={14} />, color: "#a5b4fc" },
            { label: "Active Nodes", value: stats.active, icon: <FiCheckCircle size={14} />, color: "#6ee7b7" },
            { label: "Check-In Enabled", value: stats.inbound, icon: <FiLogIn size={14} />, color: "#93c5fd" },
            { label: "Checkout Enabled", value: stats.outbound, icon: <FiLogOut size={14} />, color: "#fca5a5" },
            { label: "Returns Allowed", value: stats.returns, icon: <FiRepeat size={14} />, color: "#fcd34d" },
          ].map((kpi) => (
            <div
              key={kpi.label}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "10px",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span style={{ color: kpi.color }}>{kpi.icon}</span>
              <div>
                <div style={{ fontSize: "18px", fontWeight: 800, color: "#fff" }}>{kpi.value}</div>
                <div style={{ fontSize: "10px", color: "#a5b4fc", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {kpi.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ padding: "24px 32px" }}>
        {/* View Switcher Bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Segmented View Switcher */}
          <div
            style={{
              display: "flex",
              background: "#fff",
              border: "1.5px solid #e2e8f0",
              borderRadius: "10px",
              padding: "3px",
            }}
          >
            <button
              type="button"
              onClick={() => setOverviewTab("graph")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "7px 16px",
                borderRadius: "8px",
                border: "none",
                background: overviewTab === "graph" ? "linear-gradient(135deg,#4f46e5,#6366f1)" : "transparent",
                color: overviewTab === "graph" ? "#fff" : "#64748b",
                fontSize: "12.5px",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <MdAutoGraph size={16} /> Interactive Graph View
            </button>
            <button
              type="button"
              onClick={() => setOverviewTab("table")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "7px 16px",
                borderRadius: "8px",
                border: "none",
                background: overviewTab === "table" ? "linear-gradient(135deg,#4f46e5,#6366f1)" : "transparent",
                color: overviewTab === "table" ? "#fff" : "#64748b",
                fontSize: "12.5px",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <FiList size={16} /> Detailed Stages Table
            </button>
            <button
              type="button"
              onClick={() => setOverviewTab("theme")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "7px 16px",
                borderRadius: "8px",
                border: "none",
                background: overviewTab === "theme" ? "linear-gradient(135deg,#4f46e5,#6366f1)" : "transparent",
                color: overviewTab === "theme" ? "#fff" : "#64748b",
                fontSize: "12.5px",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <MdOutlinePalette size={16} /> Theme & Appearance
            </button>
          </div>

          {/* Search Box (Only on graph and table) */}
          {overviewTab !== "theme" && (
            <div style={{ position: "relative" }}>
              <FiSearch
                size={14}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stages by name or code..."
                style={{
                  padding: "9px 12px 9px 36px",
                  border: "1.5px solid #cbd5e1",
                  borderRadius: "9px",
                  fontSize: "13px",
                  color: "#1e293b",
                  background: "#fff",
                  outline: "none",
                  width: "260px",
                }}
              />
            </div>
          )}
        </div>

        {/* View Component Render */}
        {overviewTab === "graph" ? (
          <WorkflowGraphView
            stages={stages}
            onConfigureStage={handleConfigureStage}
            onToggleStatus={handleToggleStatus}
            onMoveOrder={handleMoveOrder}
          />
        ) : overviewTab === "table" ? (
          <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <DataTable
              columns={tableColumns}
              data={stages}
              progressPending={configsLoading}
              customStyles={customTableStyles}
              noDataComponent={
                <div style={{ padding: "48px", textAlign: "center" }}>
                  <MdOutlineLinearScale size={48} style={{ color: "#e2e8f0", marginBottom: "12px" }} />
                  <div style={{ fontSize: "16px", fontWeight: 700, color: "#94a3b8" }}>No stages configured yet</div>
                  <div style={{ fontSize: "13px", color: "#cbd5e1", marginTop: "4px" }}>
                    Click &quot;Add Stage&quot; to configure your first supply chain stage.
                  </div>
                </div>
              }
            />
          </div>
        ) : (
          <SupplyBeamThemeConfig tenantInfo={tenantInfo} />
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { font-family: 'Inter', sans-serif; box-sizing: border-box; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input:focus { border-color: #4f46e5 !important; box-shadow: 0 0 0 3px rgba(79,70,229,0.1); }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
};

export default SupplyBeamConfigManager;
